import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UserContext from "../../../state/UserContext";
import { adminHelper } from "../../../helpers/adminHelper";
import AdminDashboardSection from "./AdminDashboardSection";
import { groupDashboardItemsForDisplay } from "./adminDashboardSections";
import AdminDashboardItem from "../../../types/admin/dashboard/AdminDashboardItem";
import SearchField from "../../common/SearchField";

const SEARCH_THRESHOLD = 4;

const AdminDashboardGrid = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const dashboardItems = adminHelper.getDashboardItems(
    userContext.user.client.permissionMode,
  );
  const filteredItems = adminHelper.filterDashboardItems(
    dashboardItems,
    userContext,
  );

  const getSortLabel = (item: AdminDashboardItem) =>
    t(item.titleKey, item.title).toLocaleLowerCase();

  const getSearchLabel = (item: AdminDashboardItem) =>
    `${t(item.titleKey, item.title)} ${t(item.descriptionKey, item.description)}`.toLowerCase();

  const visibleItems = useMemo(() => {
    const query = searchTerm?.trim().toLowerCase();
    if (!query) {
      return filteredItems;
    }

    return filteredItems.filter((item) => getSearchLabel(item).includes(query));
  }, [filteredItems, searchTerm, t]);

  const displayGroups = groupDashboardItemsForDisplay(
    visibleItems,
    getSortLabel,
  );

  const showSearch = filteredItems.length > SEARCH_THRESHOLD;
  const hasVisibleResults = visibleItems.length > 0;

  return (
    <div className="pt-4">
      {showSearch && (
        <div className="mb-6 flex justify-end">
          <SearchField
            id="admin-dashboard-search"
            isLoading={false}
            onSearch={setSearchTerm}
            loaderColor={userContext.user.client.primaryHexColour}
            includeSetWidth={false}
            outerContainerClassName="w-full sm:w-auto sm:shrink-0"
            className="w-full sm:max-w-xs lg:max-w-sm"
            placeholderText={t(
              "Pages.Admin.Dashboard.SearchPlaceholder",
              "Find an admin tool...",
            )}
          />
        </div>
      )}

      {!hasVisibleResults && (
        <p className="mb-6 text-sm text-gray-600">
          {t(
            "Pages.Admin.Dashboard.NoSearchResults",
            "No admin tools match your search.",
          )}
        </p>
      )}

      <div className="space-y-8">
        {displayGroups.map((group) => (
          <AdminDashboardSection
            key={group.section.id}
            section={group.section}
            items={group.items}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminDashboardGrid;
